/**
 * FP&A: importação do Realizado pela tela (ADR 0032, seção 10). Prepara as células lidas do .xlsx
 * para o leitor (fpa-realizado.ts) e valida as entradas da API. Sem ponto flutuante: os números
 * da planilha chegam como texto e são arredondados em decimal exato.
 */
import { z } from 'zod';
import { REALIZADO_COLUMNS, type Celula } from './fpa-realizado.ts';
import { fromScaled } from './money.ts';

/** Nome da aba lida (só ela: as outras abas da planilha ficam fora). */
export const REALIZADO_SHEET = 'Realizado';
/** Layout aceito hoje: a exportação atual do NetSuite. Outros layouts entram como novas versões. */
export const REALIZADO_LAYOUT = 'netsuite-realizado-v1';

const NUMBER_RE = /^(-)?(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;

/**
 * Situação do centro de custo encontrado na importação, na competência da linha (ADR 0032, seção
 * 12). Ativo hoje: segue normal. Inativo hoje, mas com a vigência terminando na competência ou
 * depois: estava ativo naquele mês e foi desativado mais tarde, então a linha não fica inválida
 * sozinha (só ganha um alerta). Inativo sem fim de vigência, ou terminado antes da competência:
 * vai para validação. O início da vigência não entra na conta, porque nasce com a data do cadastro.
 */
export function costCenterAtCompetence(
  center: { isActive: boolean; validUntil: string | null },
  competence: string | null,
): 'active' | 'active_at_competence' | 'inactive' {
  if (center.isActive) return 'active';
  if (competence && center.validUntil && center.validUntil >= `${competence.slice(0, 7)}-01`) {
    return 'active_at_competence';
  }
  return 'inactive';
}

/**
 * Texto numérico do Excel ("1133.6500000000001", "-1.5E-2") arredondado para 4 casas, metade para
 * longe do zero, sem passar por ponto flutuante. Devolve null se não for número.
 */
export function roundDecimalText(text: string): string | null {
  const m = NUMBER_RE.exec(text.trim());
  if (!m) return null;
  const [, sign, int, frac = '', exp = '0'] = m;
  let digits = `${int}${frac}`.replace(/^0+(?=\d)/, '');
  // Posição da vírgula contada a partir do fim.
  let scale = frac.length - Number(exp);
  if (scale < 0) {
    digits += '0'.repeat(-scale);
    scale = 0;
  }
  let value = BigInt(digits || '0');
  if (scale > 4) {
    const div = 10n ** BigInt(scale - 4);
    const q = value / div;
    value = (value % div) * 2n >= div ? q + 1n : q;
  } else {
    value *= 10n ** BigInt(4 - scale);
  }
  return fromScaled(sign && value !== 0n ? -value : value);
}

/**
 * Linhas do .xlsx (texto) → células para o leitor: a competência numérica vira o número de série
 * da data do Excel (não é dinheiro); os valores viram texto decimal arredondado em 4 casas.
 */
export function realizadoCellsFromXlsx(rows: readonly (readonly (string | null)[])[]): Celula[][] {
  const [header = [], ...data] = rows;
  const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').trim().toUpperCase();
  const idx = (name: string) => header.findIndex((h) => norm(h ?? '') === norm(name));
  const competence = idx(REALIZADO_COLUMNS.competencia);
  const money = [idx(REALIZADO_COLUMNS.valorOriginal), idx(REALIZADO_COLUMNS.valorConvertidoBrl)];
  return [
    [...header],
    ...data.map((row) =>
      row.map((cell, i): Celula => {
        if (cell === null) return null;
        if (i === competence && /^\d+(\.\d+)?$/.test(cell.trim())) return Number(cell);
        if (money.includes(i)) return roundDecimalText(cell) ?? cell;
        return cell;
      }),
    ),
  ];
}

const uuid = z.uuid();
const optionalText = z
  .string()
  .trim()
  .max(1000)
  .nullish()
  .transform((v) => v || null);

export const fpaImportReviewInput = z
  .object({
    decision: z.enum(['approved', 'rejected', 'pending']),
    note: optionalText,
  })
  .refine((v) => v.decision === 'pending' || (v.note?.length ?? 0) >= 3, {
    path: ['note'],
    message: 'Registre o motivo da decisão',
  });

export const fpaImportApplyInput = z.object({ versionId: uuid });

/** A versão do mapeamento que a aba mostra: o rascunho em edição ou, sem ele, a ativa. */
export const shownMappingVersion = <V extends { status: string }>(versions: readonly V[]) =>
  versions.find((v) => v.status === 'draft') ?? versions.find((v) => v.status === 'active');

/** As regras de uma combinação original, com ou sem condições, sem diferenciar maiúsculas. */
export const rulesOfCombination = <
  R extends { classificationOriginal: string; expenseTypeOriginal: string },
>(
  rules: readonly R[],
  classificationOriginal: string,
  expenseTypeOriginal: string,
) =>
  rules.filter(
    (r) =>
      r.classificationOriginal.toUpperCase() === classificationOriginal.toUpperCase() &&
      r.expenseTypeOriginal.toUpperCase() === expenseTypeOriginal.toUpperCase(),
  );

/** Combinações sem nenhuma regra: o filtro "sem regra" da aba e o cartão da Visão geral. */
export const unmappedCombinations = <
  C extends { classificationOriginal: string | null; expenseTypeOriginal: string | null },
>(
  combinations: readonly C[],
  rules: readonly { classificationOriginal: string; expenseTypeOriginal: string }[],
) =>
  combinations.filter(
    (c) =>
      rulesOfCombination(rules, c.classificationOriginal ?? '', c.expenseTypeOriginal ?? '')
        .length === 0,
  );

export const fpaImportRowsQuery = z.object({
  treatment: z.enum(['actual', 'ignored', 'validation']).optional(),
  review: z.enum(['pending', 'approved', 'rejected']).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  /** Busca: número da linha, ID da transação, descrição, centro ou nomes originais. */
  q: z.string().trim().max(100).optional(),
  /** Só as linhas com ajuste gerencial vigente (ADR 0032, 10.5). */
  adjusted: z.enum(['yes']).optional(),
  /** Só as que entram no Realizado (realizado ou aprovadas). */
  actual: z.enum(['yes']).optional(),
  /** Fretes históricos sem finalidade registrada, para revisão (ADR 0032, 13). */
  freight: z.enum(['review']).optional(),
});

export const fpaMappingInput = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9._-]{1,39}$/, 'Use de 2 a 40 letras, números, ., - ou _'),
  name: z.string().trim().min(1).max(200),
  description: optionalText,
  /** Começar com as regras de outra versão do mapeamento. */
  copyFromId: uuid.nullish().transform((v) => v ?? null),
});

export const fpaMappingRuleInput = z.object({
  classificationOriginal: z.string().trim().min(1).max(200),
  expenseTypeOriginal: z.string().trim().min(1).max(200),
  classificationId: uuid.nullish().transform((v) => v ?? null),
  expenseTypeId: uuid.nullish().transform((v) => v ?? null),
  managementAccountId: uuid.nullish().transform((v) => v ?? null),
  inIncomeStatement: z
    .boolean()
    .nullish()
    .transform((v) => v ?? null),
  notes: optionalText,
  /** Ordem de avaliação: vale a primeira prioridade em que a regra casa (ADR 0032, 10.3). */
  priority: z.number().int().min(1).max(1000).default(100),
  /** Condições opcionais: conta contábil, centros de custo e trecho da descrição. */
  accountCode: z
    .string()
    .trim()
    .max(60)
    .nullish()
    .transform((v) => v || null),
  costCenterCodes: z
    .array(z.string().trim().min(1).max(30))
    .max(500)
    .nullish()
    .transform((v) => (v && v.length ? [...new Set(v)].sort() : null)),
  descriptionContains: z
    .string()
    .trim()
    .max(100)
    .nullish()
    .transform((v) => (v && v.length >= 2 ? v : null)),
  ruleCode: z
    .string()
    .trim()
    .max(20)
    .nullish()
    .transform((v) => v || null),
  origin: z
    .string()
    .trim()
    .max(300)
    .nullish()
    .transform((v) => v || null),
  /** Proposta (ex.: função do centro presumida) mapeia, mas fica marcada até a confirmação. */
  status: z.enum(['confirmed', 'proposed']).default('confirmed'),
});
export type FpaMappingRuleInput = z.infer<typeof fpaMappingRuleInput>;

/** Texto comparável: sem acentos, sem diferença de maiúsculas e com espaços simples. */
const comparable = (s: string | null | undefined) =>
  (s ?? '').normalize('NFD').replace(/\p{M}/gu, '').trim().toUpperCase().replace(/\s+/g, ' ');

/** Código antes de " - " ("4.2.2.06.000004 - VARIAÇÃO CAMBIAL" → "4.2.2.06.000004"). */
export const codeOf = (s: string | null | undefined) => comparable((s ?? '').split(' - ')[0]);

export interface FpaRuleConditions {
  classificationOriginal: string;
  expenseTypeOriginal: string;
  priority: number;
  accountCode: string | null;
  costCenterCodes: string[] | null;
  descriptionContains: string | null;
}
export interface FpaRuleTarget {
  classificationId: string | null;
  expenseTypeId: string | null;
  managementAccountId: string | null;
}

/**
 * Regra do de/para para uma linha do Realizado (ADR 0032, 10.3). Casa o par original e todas as
 * condições preenchidas; vale a primeira prioridade com regra. Duas regras da mesma prioridade com
 * destinos diferentes deixam a linha ambígua (sem regra): nada é decidido por palpite.
 */
export function matchMappingRule<R extends FpaRuleConditions & FpaRuleTarget>(
  rules: readonly R[],
  row: {
    classificationOriginal: string | null;
    expenseTypeOriginal: string | null;
    account: string | null;
    costCenterCode: string | null;
    description: string | null;
  },
): { rule: R | null; ambiguous: boolean } {
  const cl = comparable(row.classificationOriginal);
  const et = comparable(row.expenseTypeOriginal);
  const account = codeOf(row.account);
  const center = codeOf(row.costCenterCode);
  const description = comparable(row.description);
  const hits = rules.filter(
    (r) =>
      comparable(r.classificationOriginal) === cl &&
      comparable(r.expenseTypeOriginal) === et &&
      (!r.accountCode || codeOf(r.accountCode) === account) &&
      (!r.costCenterCodes || r.costCenterCodes.some((c) => codeOf(c) === center)) &&
      (!r.descriptionContains || description.includes(comparable(r.descriptionContains))),
  );
  if (hits.length === 0) return { rule: null, ambiguous: false };
  const first = Math.min(...hits.map((r) => r.priority));
  const top = hits.filter((r) => r.priority === first);
  const target = (r: R) => `${r.classificationId}|${r.expenseTypeId}|${r.managementAccountId}`;
  if (new Set(top.map(target)).size > 1) return { rule: null, ambiguous: true };
  return { rule: top[0]!, ambiguous: false };
}
